import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, User, Calendar, Users as UsersIcon, ClipboardList,
  TrendingUp, History, Plus, X, Link2, FileText, AlertTriangle,
  CheckCircle2, ClipboardCheck,
} from "lucide-react";

const STATUS_META = {
  not_started:        { label: "Not Started",        tone: "border-slate-300 bg-slate-50 text-slate-700" },
  in_progress:        { label: "In Progress",        tone: "border-sky-300 bg-sky-50 text-sky-800" },
  on_track:           { label: "On Track",           tone: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  at_risk:            { label: "At Risk",            tone: "border-amber-300 bg-amber-50 text-amber-800" },
  achieved:           { label: "Achieved",           tone: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  partially_achieved: { label: "Partially Achieved", tone: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  not_achieved:       { label: "Not Achieved",       tone: "border-rose-300 bg-rose-50 text-rose-800" },
  paused:             { label: "Paused",             tone: "border-slate-300 bg-slate-50 text-slate-700" },
  closed:             { label: "Closed",             tone: "border-slate-300 bg-slate-50 text-slate-700" },
};

const PROGRESS_LEVELS = [
  { key: "achieved_independently",       label: "Achieved Independently",       tone: "text-emerald-700" },
  { key: "achieved_minimal_support",     label: "Achieved with Minimal Support",tone: "text-emerald-700" },
  { key: "achieved_moderate_support",    label: "Achieved with Moderate Support",tone: "text-sky-700" },
  { key: "achieved_significant_support", label: "Achieved with Significant Support", tone: "text-amber-700" },
  { key: "attempted",                    label: "Attempted",                    tone: "text-slate-700" },
  { key: "not_attempted",                label: "Not Attempted",                tone: "text-slate-500" },
  { key: "declined",                     label: "Declined",                     tone: "text-slate-500" },
  { key: "unable",                       label: "Unable",                       tone: "text-slate-500" },
  { key: "not_applicable",               label: "Not Applicable",               tone: "text-slate-400" },
];

const EVIDENCE_TYPES = [
  { key: "daily_note",  label: "Daily Note" },
  { key: "observation", label: "Observation" },
  { key: "appointment", label: "Appointment" },
  { key: "care_plan",   label: "Care Plan" },
  { key: "risk",        label: "Risk Assessment" },
  { key: "document",    label: "Document" },
  { key: "incident",    label: "Incident" },
  { key: "vitals",      label: "Vitals" },
];

const REVIEW_DECISIONS = [
  { key: "continue",           label: "Continue" },
  { key: "modify",             label: "Modify" },
  { key: "achieved",           label: "Achieved" },
  { key: "partially_achieved", label: "Partially Achieved" },
  { key: "pause",              label: "Pause" },
  { key: "not_achieved",       label: "Not Achieved" },
  { key: "close",              label: "Close" },
];

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "progress", label: "Progress" },
  { key: "evidence", label: "Evidence" },
  { key: "reviews",  label: "Reviews" },
  { key: "audit",    label: "History / Audit" },
];

export default function OutcomeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canWrite = ["admin", "manager", "senior_care_worker", "care_worker"].includes(user?.role);
  const canReview = ["admin", "manager", "senior_care_worker"].includes(user?.role);
  const [outcome, setOutcome] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/outcomes/${id}`);
      setOutcome(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load outcome"); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-slate-500" data-testid="outcome-detail-loading">Loading outcome…</div>;
  if (!outcome) return <div className="p-6 text-slate-500" data-testid="outcome-detail-missing">Outcome not found.</div>;

  const status = STATUS_META[outcome.status] || STATUS_META.not_started;

  return (
    <div className="p-4 lg:p-6 max-w-[1200px] mx-auto" data-testid="outcome-detail-page">
      <Link to="/outcomes" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-700 mb-3" data-testid="btn-back-to-outcomes">
        <ArrowLeft className="w-3.5 h-3.5" /> All outcomes
      </Link>

      <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900" data-testid="outcome-title">{outcome.title}</h1>
            <div className="mt-1 text-sm text-slate-600 flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-slate-400" />
              <Link to={`/clients/${outcome.service_user_id}`} className="text-emerald-700 hover:underline" data-testid="outcome-service-user-link">
                {outcome.service_user_name || outcome.service_user_id}
              </Link>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500">{outcome.category_name || "—"}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] uppercase tracking-widest border rounded px-2 py-0.5 font-semibold ${status.tone}`} data-testid="outcome-status-badge">{status.label}</span>
            {typeof outcome.progress_value === "number" && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Progress</div>
                <div className="font-heading text-xl font-semibold tabular-nums text-slate-900" data-testid="outcome-progress-value">{outcome.progress_value}%</div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-[12px]">
          <MetaCell icon={Calendar} label="Started"     value={outcome.start_date}         />
          <MetaCell icon={Calendar} label="Target"      value={outcome.target_date}        />
          <MetaCell icon={Calendar} label="Next Review" value={outcome.next_review_date}   />
          <MetaCell icon={UsersIcon} label="Key Worker" value={outcome.key_worker_name}    />
          <MetaCell icon={ClipboardList} label="Care Plan" value={outcome.care_plan_title} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="border-b border-slate-100 flex items-center gap-0 px-2 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-[12px] font-medium px-3 py-2.5 border-b-2 whitespace-nowrap ${tab === t.key ? "border-emerald-500 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              data-testid={`tab-${t.key}`}>{t.label}</button>
          ))}
        </div>

        <div className="p-5">
          {tab === "overview" && <OverviewTab outcome={outcome} />}
          {tab === "progress" && <ProgressTab outcome={outcome} canWrite={canWrite} onChanged={load} />}
          {tab === "evidence" && <EvidenceTab outcome={outcome} canWrite={canWrite} />}
          {tab === "reviews"  && <ReviewsTab outcome={outcome} canReview={canReview} onChanged={load} />}
          {tab === "audit"    && <AuditTab outcome={outcome} />}
        </div>
      </div>
    </div>
  );
}

function MetaCell({ icon: Icon, label, value }) {
  return (
    <div className="border border-slate-100 rounded p-2">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-sm text-slate-800 mt-0.5">{value || "—"}</div>
    </div>
  );
}

function OverviewTab({ outcome }) {
  const rows = [
    { label: "What matters to me",  value: outcome.person_statement, italic: true },
    { label: "Desired outcome",     value: outcome.desired_outcome },
    { label: "Success measure",     value: outcome.success_measure },
    { label: "Support strategy",    value: outcome.support_strategy },
  ];
  return (
    <div className="space-y-4">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5">{r.label}</div>
          <div className={`text-sm text-slate-800 whitespace-pre-wrap ${r.italic ? "italic" : ""}`}
            data-testid={`overview-${r.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
            {r.value || <span className="text-slate-400 not-italic">Not recorded yet</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progress tab — timeline + sparkline + Record Progress modal
// ---------------------------------------------------------------------------
function ProgressTab({ outcome, canWrite, onChanged }) {
  const [items, setItems] = useState([]);
  const [trend, setTrend] = useState({ points: [] });
  const [window, setWindow] = useState("3m");
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, t] = await Promise.all([
        api.get(`/outcomes/${outcome.id}/progress`),
        api.get(`/outcomes/${outcome.id}/trend`, { params: { window } }),
      ]);
      setItems(r.data.items || []);
      setTrend(t.data || { points: [] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load progress"); }
  }, [outcome.id, window]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="progress-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="uppercase tracking-widest text-slate-500 font-semibold">Trend</span>
          <select value={window} onChange={(e) => setWindow(e.target.value)}
            className="h-7 px-2 border border-slate-300 rounded-md" data-testid="trend-window">
            <option value="30d">Last 30 days</option>
            <option value="3m">Last 3 months</option>
            <option value="6m">Last 6 months</option>
            <option value="12m">Last 12 months</option>
            <option value="all">All time</option>
          </select>
        </div>
        {canWrite && (
          <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-[12px]" data-testid="btn-record-progress">
            <Plus className="w-3.5 h-3.5 mr-1" /> Record Progress
          </Button>
        )}
      </div>

      <Sparkline points={trend.points || []} />

      {items.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded" data-testid="progress-empty">
          No progress recorded yet.
        </div>
      ) : (
        <div className="relative pl-5 border-l border-slate-200" data-testid="progress-timeline">
          {items.map((r) => {
            const lvl = PROGRESS_LEVELS.find((x) => x.key === r.progress_level);
            return (
              <div key={r.id} className="mb-4 relative" data-testid={`progress-entry-${r.id}`}>
                <div className="absolute -left-[27px] top-1.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white shadow" />
                <div className="text-[11px] tabular-nums text-slate-500">
                  {r.date}{r.time ? ` · ${r.time}` : ""} · {r.staff_name || r.staff_email}
                </div>
                <div className={`text-sm font-medium mt-0.5 ${lvl?.tone || "text-slate-700"}`}>{lvl?.label || r.progress_level}</div>
                {typeof r.progress_value === "number" && (
                  <div className="text-[11px] tabular-nums text-slate-500">Progress → {r.progress_value}%</div>
                )}
                <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{r.note}</div>
                {r.service_user_perspective && (
                  <div className="text-[12px] text-slate-600 italic mt-1">Person said: “{r.service_user_perspective}”</div>
                )}
                {r.staff_reflection && (
                  <div className="text-[12px] text-slate-500 mt-1">Staff reflection: {r.staff_reflection}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <RecordProgressModal outcome={outcome}
          onClose={() => setShowModal(false)}
          onSaved={async () => { setShowModal(false); await load(); await onChanged(); toast.success("Progress recorded"); }} />
      )}
    </div>
  );
}

function Sparkline({ points }) {
  if (!points.length) return (
    <div className="text-[11px] text-slate-400 border border-dashed border-slate-200 rounded p-4 text-center" data-testid="sparkline-empty">
      No trend data yet — record a couple of progress entries to see the line.
    </div>
  );
  const w = 640, h = 80, pad = 6;
  const vals = points.map((p) => p.progress_value);
  const min = 0, max = 100;
  const step = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const path = points.map((p, i) => {
    const x = pad + i * step;
    const y = h - pad - ((p.progress_value - min) / (max - min)) * (h - pad * 2);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = points[points.length - 1];
  return (
    <div className="border border-slate-200 rounded p-3" data-testid="sparkline">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20">
        <line x1="0" y1={h - pad} x2={w} y2={h - pad} stroke="#e2e8f0" />
        <path d={path} stroke="#10b981" strokeWidth="2" fill="none" strokeLinejoin="round" />
        {points.map((p, i) => {
          const x = pad + i * step;
          const y = h - pad - ((p.progress_value - 0) / 100) * (h - pad * 2);
          return <circle key={i} cx={x} cy={y} r="2.5" fill="#10b981" />;
        })}
      </svg>
      <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1 tabular-nums">
        <span>{points[0].date}</span>
        <span className="font-semibold text-emerald-700">Latest: {last.progress_value}%</span>
        <span>{last.date}</span>
      </div>
    </div>
  );
}

function RecordProgressModal({ outcome, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().slice(0, 5),
    progress_level: "achieved_minimal_support",
    note: "",
    service_user_perspective: "",
    staff_reflection: "",
    progress_value: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.note.trim()) { toast.error("Note is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.progress_value === "") delete payload.progress_value;
      else payload.progress_value = Number(payload.progress_value);
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post(`/outcomes/${outcome.id}/progress`, payload);
      await onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-6 overflow-auto" data-testid="record-progress-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Record Progress</div>
            <div className="text-sm text-slate-600 mt-0.5">{outcome.title}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800" data-testid="btn-close-progress-modal"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-[11px] text-slate-600">Date
            <input type="date" required value={form.date} onChange={set("date")}
              className="mt-1 w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="progress-date" />
          </label>
          <label className="text-[11px] text-slate-600">Time
            <input type="time" value={form.time} onChange={set("time")}
              className="mt-1 w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="progress-time" />
          </label>
          <label className="text-[11px] text-slate-600 md:col-span-2">Progress level
            <select required value={form.progress_level} onChange={set("progress_level")}
              className="mt-1 w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="progress-level">
              {PROGRESS_LEVELS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] text-slate-600 md:col-span-2">What happened? *
            <textarea rows={3} required value={form.note} onChange={set("note")}
              placeholder="e.g. Bayo prepared cereal, toast and a drink independently with one verbal reminder."
              className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="progress-note" />
          </label>
          <label className="text-[11px] text-slate-600 md:col-span-2">What did the person say / how did they feel?
            <textarea rows={2} value={form.service_user_perspective} onChange={set("service_user_perspective")}
              placeholder='"I did it myself today."'
              className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="progress-person-perspective" />
          </label>
          <label className="text-[11px] text-slate-600 md:col-span-2">Staff reflection (optional)
            <textarea rows={2} value={form.staff_reflection} onChange={set("staff_reflection")}
              className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="progress-staff-reflection" />
          </label>
          <label className="text-[11px] text-slate-600">Override progress % (optional)
            <input type="number" min={0} max={100} value={form.progress_value} onChange={set("progress_value")}
              placeholder="Leave blank to auto-derive"
              className="mt-1 w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="progress-value-override" />
          </label>
          <div className="col-span-full flex items-center justify-end gap-2 pt-2 border-t border-slate-100 mt-2">
            <button type="button" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800">Cancel</button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-submit-progress">
              {saving ? "Saving…" : "Save Progress"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Evidence tab
// ---------------------------------------------------------------------------
function EvidenceTab({ outcome, canWrite }) {
  const [items, setItems] = useState([]);
  const [showLinker, setShowLinker] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/outcomes/${outcome.id}/evidence`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }, [outcome.id]);
  useEffect(() => { load(); }, [load]);
  const unlink = async (link_id) => {
    if (!window.confirm("Unlink this evidence?")) return;
    try {
      await api.delete(`/outcomes/${outcome.id}/evidence/${link_id}`);
      await load();
      toast.success("Evidence unlinked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="evidence-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">Link existing daily notes, appointments, care plans and documents to this outcome as evidence of progress.</div>
        {canWrite && (
          <Button onClick={() => setShowLinker(true)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-[12px]" data-testid="btn-link-evidence">
            <Link2 className="w-3.5 h-3.5 mr-1" /> Link Evidence
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded" data-testid="evidence-empty">
          No evidence linked yet.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded" data-testid="evidence-list">
          {items.map((e) => (
            <li key={e.id} className="px-3 py-2 flex items-start gap-3" data-testid={`evidence-item-${e.id}`}>
              <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{e.evidence_type.replace("_"," ")}</div>
                <div className="text-sm text-slate-800">{e.evidence_summary}</div>
                <div className="text-[11px] text-slate-500">{e.evidence_date || "—"} · linked by {e.linked_by_name || e.linked_by}</div>
              </div>
              {canWrite && (
                <button onClick={() => unlink(e.id)} className="text-[11px] text-slate-400 hover:text-red-700" data-testid={`btn-unlink-${e.id}`}>Unlink</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {showLinker && (
        <LinkEvidenceModal outcome={outcome}
          onClose={() => setShowLinker(false)}
          onLinked={async () => { setShowLinker(false); await load(); toast.success("Evidence linked"); }} />
      )}
    </div>
  );
}

function LinkEvidenceModal({ outcome, onClose, onLinked }) {
  const [type, setType] = useState("daily_note");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/outcomes/${outcome.id}/evidence-candidates`,
        { params: { evidence_type: type, q: q || undefined } });
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  }, [outcome.id, type, q]);
  useEffect(() => { load(); }, [load]);
  const pick = async (id) => {
    try {
      await api.post(`/outcomes/${outcome.id}/evidence`,
        { evidence_type: type, evidence_id: id });
      await onLinked();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-6 overflow-auto" data-testid="link-evidence-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Link Evidence</div>
            <div className="text-sm text-slate-600 mt-0.5">{outcome.title}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <select value={type} onChange={(e) => setType(e.target.value)}
              className="h-8 px-2 border border-slate-300 rounded-md text-sm" data-testid="evidence-type-picker">
              {EVIDENCE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              className="h-8 px-2 border border-slate-300 rounded-md text-sm flex-1" data-testid="evidence-search" />
          </div>
          {loading ? <div className="text-sm text-slate-500">Loading…</div> :
            items.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6" data-testid="candidates-empty">
                No {type.replace("_"," ")} records available for this service user.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded max-h-72 overflow-auto">
                {items.map((it) => (
                  <li key={it.id} className="px-3 py-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-800 truncate">{it.summary}</div>
                      <div className="text-[11px] text-slate-500">{it.date || "—"}</div>
                    </div>
                    <button type="button" disabled={it.already_linked} onClick={() => pick(it.id)}
                      className={`text-[11px] px-2 py-1 rounded border ${it.already_linked ? "bg-slate-50 text-slate-400 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"}`}
                      data-testid={`btn-link-candidate-${it.id}`}>
                      {it.already_linked ? "Linked" : "Link"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reviews tab
// ---------------------------------------------------------------------------
function ReviewsTab({ outcome, canReview, onChanged }) {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/outcomes/${outcome.id}/reviews`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }, [outcome.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="reviews-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">Formal reviews capture what has gone well, the person's own words, and the review decision.</div>
        {canReview && (
          <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-[12px]" data-testid="btn-review-outcome">
            <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Review Outcome
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded" data-testid="reviews-empty">
          No reviews yet.
        </div>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {items.map((r) => (
            <div key={r.id} className="border border-slate-200 rounded p-3" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                  {r.review_date} · {r.reviewed_by_name || r.reviewed_by}
                </div>
                <span className="text-[10px] uppercase tracking-widest border border-emerald-300 bg-emerald-50 text-emerald-800 rounded px-2 py-0.5 font-semibold">{r.decision}</span>
              </div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-[13px]">
                {r.what_went_well && <ReviewCell label="What went well" value={r.what_went_well} />}
                {r.what_did_not && <ReviewCell label="What didn't" value={r.what_did_not} />}
                {r.person_says && <ReviewCell label="Person says" value={r.person_says} italic />}
                {r.family_says && <ReviewCell label="Family / representatives say" value={r.family_says} italic />}
                {r.staff_observations && <ReviewCell label="Staff observations" value={r.staff_observations} />}
                {r.comments && <ReviewCell label="Comments" value={r.comments} />}
              </div>
              {r.next_review_date && <div className="mt-2 text-[11px] text-slate-500">Next review: {r.next_review_date}</div>}
            </div>
          ))}
        </div>
      )}
      {showModal && (
        <ReviewModal outcome={outcome}
          onClose={() => setShowModal(false)}
          onSaved={async () => { setShowModal(false); await load(); await onChanged(); toast.success("Review saved"); }} />
      )}
    </div>
  );
}

function ReviewCell({ label, value, italic }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm text-slate-800 whitespace-pre-wrap ${italic ? "italic" : ""}`}>{value}</div>
    </div>
  );
}

function ReviewModal({ outcome, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    review_date: new Date().toISOString().slice(0, 10),
    what_went_well: "", what_did_not: "", person_says: "", family_says: "",
    staff_observations: "", decision: "continue", comments: "", next_review_date: "",
    still_important: true, need_changed: false, care_plan_needs_update: false,
    risk_needs_review: false, success_measure_appropriate: true,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post(`/outcomes/${outcome.id}/reviews`, payload);
      await onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-6 overflow-auto" data-testid="review-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-3xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Formal Review</div>
            <div className="text-sm text-slate-600 mt-0.5">{outcome.title}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-[11px] text-slate-600">Review date
            <input type="date" required value={form.review_date} onChange={set("review_date")}
              className="mt-1 w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="review-date" />
          </label>
          <label className="text-[11px] text-slate-600">Decision *
            <select required value={form.decision} onChange={set("decision")}
              className="mt-1 w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="review-decision">
              {REVIEW_DECISIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </label>
          <Textareaish label="What has gone well?"  value={form.what_went_well}     set={set("what_went_well")}     testid="review-well" />
          <Textareaish label="What has not gone well?" value={form.what_did_not}   set={set("what_did_not")}       testid="review-not-well" />
          <Textareaish label="What does the person say?" value={form.person_says}  set={set("person_says")}        italic testid="review-person" />
          <Textareaish label="What do family / representatives say?" value={form.family_says} set={set("family_says")} italic testid="review-family" />
          <Textareaish label="What do staff observe?" value={form.staff_observations} set={set("staff_observations")} testid="review-staff-obs" />
          <label className="text-[11px] text-slate-600">Next review date
            <input type="date" value={form.next_review_date} onChange={set("next_review_date")}
              className="mt-1 w-full h-9 px-2 border border-slate-300 rounded-md text-sm" data-testid="review-next-date" />
          </label>
          <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-[12px] text-slate-700">
            <Check k="still_important" label="Still important to person" form={form} setForm={setForm} />
            <Check k="need_changed" label="Need has changed" form={form} setForm={setForm} />
            <Check k="care_plan_needs_update" label="Care plan needs update" form={form} setForm={setForm} />
            <Check k="risk_needs_review" label="Risk assessment needs review" form={form} setForm={setForm} />
            <Check k="success_measure_appropriate" label="Success measure still appropriate" form={form} setForm={setForm} />
          </div>
          <label className="text-[11px] text-slate-600 md:col-span-2">Comments / rationale
            <textarea rows={3} value={form.comments} onChange={set("comments")}
              className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" data-testid="review-comments" />
          </label>
          <div className="col-span-full flex items-center justify-end gap-2 pt-2 border-t border-slate-100 mt-2">
            <button type="button" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800">Cancel</button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-submit-review">
              {saving ? "Saving…" : "Save Review"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Textareaish({ label, value, set, testid, italic }) {
  return (
    <label className="text-[11px] text-slate-600 md:col-span-2">{label}
      <textarea rows={2} value={value} onChange={set}
        className={`mt-1 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm ${italic ? "italic" : ""}`} data-testid={testid} />
    </label>
  );
}

function Check({ k, label, form, setForm }) {
  return (
    <label className="flex items-center gap-2">
      <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} data-testid={`review-check-${k}`} />
      <span>{label}</span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Audit tab
// ---------------------------------------------------------------------------
function AuditTab({ outcome }) {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => {
    try {
      const r = await api.get(`/audit/events`, { params: { module: "outcomes", client_id: outcome.service_user_id } });
      const rows = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setItems(rows.filter((x) => x.record_id === outcome.id));
    } catch { /* soft-fail */ }
  })(); }, [outcome.id, outcome.service_user_id]);
  return (
    <div className="space-y-2" data-testid="audit-tab">
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded" data-testid="audit-empty">No audit entries yet.</div>
      ) : items.map((a) => (
        <div key={a.id || a._id} className="border border-slate-200 rounded p-2 text-[13px]" data-testid={`audit-${a.id || ""}`}>
          <div className="text-[11px] text-slate-500 tabular-nums">
            {(a.at || a.created_at || "").slice(0, 19)} · {a.actor_name || a.actor_email || a.by_name || a.by_email}
          </div>
          <div className="text-slate-800 mt-0.5">
            <span className="font-medium">{a.action}</span>: {a.summary || ""}
          </div>
        </div>
      ))}
    </div>
  );
}
